import React, { useEffect, useRef, useState } from 'react';
import { X, Send } from 'lucide-react';

/**
 * Luxury WhatsApp widget for Rajpurohit Perfumes.
 * Replaces the generic AiSensy plugin widget with brand-matched styling,
 * an attention-catching animated pill, and quick-reply chips.
 */

const PHONE = '918866655699';

// Rotating teasers on the collapsed pill
const TEASERS = [
  { dot: 'Free Sample',    text: 'FREE 8ML on orders ₹2,499+' },
  { dot: 'Save 10%',       text: 'Flat 10% OFF prepaid orders' },
  { dot: 'Need help?',     text: 'Find your signature scent' },
];

// Quick-reply chips inside the panel
const CHIPS = [
  { emoji: '🎁', label: 'Claim my free 8ml sample',    msg: "Hi! I'd like to claim the free 8ML sample on my first order." },
  { emoji: '🔥', label: 'Show me your best sellers',    msg: 'Hi! Can you show me your best-selling fragrances?' },
  { emoji: '💬', label: 'Help me choose a fragrance',   msg: "Hi! I'm not sure which fragrance suits me. Can you help?" },
  { emoji: '📦', label: 'Track my order',               msg: 'Hi! I would like to track my order.' },
];

const waLink = (msg) =>
  `https://wa.me/${PHONE}?text=${encodeURIComponent(msg)}`;

export default function WhatsAppWidget() {
  const [open, setOpen]       = useState(false);
  const [teaser, setTeaser]   = useState(0);
  const [visible, setVisible] = useState(false);
  const [typed, setTyped]     = useState('');
  const panelRef              = useRef(null);

  // Delay first appearance so it doesn't fight the hero
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 2500);
    return () => clearTimeout(t);
  }, []);

  // Rotate teaser copy every 5s while collapsed
  useEffect(() => {
    if (open) return;
    const t = setInterval(() => setTeaser((i) => (i + 1) % TEASERS.length), 5000);
    return () => clearInterval(t);
  }, [open]);

  // Typewriter effect on the teaser text
  useEffect(() => {
    if (open) return;
    const full = TEASERS[teaser].text;
    setTyped('');
    let i = 0;
    const t = setInterval(() => {
      i += 1;
      setTyped(full.slice(0, i));
      if (i >= full.length) clearInterval(t);
    }, 28);
    return () => clearInterval(t);
  }, [teaser, open]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="fixed z-[80] left-3 sm:left-5 bottom-[5.5rem] md:bottom-6 font-sans">
      {/* ── EXPANDED PANEL ── */}
      {open && (
        <div
          ref={panelRef}
          className="mb-3 w-[330px] max-w-[calc(100vw-1.5rem)] rounded-2xl overflow-hidden shadow-[0_24px_60px_rgba(26,24,20,0.28)] animate-[waIn_.28s_cubic-bezier(.22,1,.36,1)]"
          style={{ background: '#FBF7F0' }}
        >
          {/* Header */}
          <div className="relative px-4 py-4 flex items-start gap-3"
               style={{ background: 'linear-gradient(135deg,#1A2332 0%,#2c3e5b 100%)' }}>
            <div className="w-11 h-11 rounded-full bg-[#F5EFE6] flex items-center justify-center shrink-0 overflow-hidden">
              <img src="/branded/logo-mark.webp" alt=""
                   className="w-full h-full object-cover"
                   onError={(e) => { e.currentTarget.style.display = 'none';
                                     e.currentTarget.parentElement.textContent = 'RP'; }} />
            </div>
            <div className="flex-1 min-w-0 pt-0.5">
              <p className="text-[#F5EFE6] text-[15px] font-medium leading-tight">Rajpurohit Perfumes</p>
              <p className="flex items-center gap-1.5 mt-1 text-[#C9A961] text-[11px]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#25D366] animate-pulse" />
                Usually replies in a few minutes
              </p>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close chat"
                    className="text-[#F5EFE6]/60 hover:text-[#F5EFE6] transition shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Greeting bubble */}
          <div className="px-4 pt-4">
            <div className="relative bg-white rounded-xl rounded-tl-sm px-3.5 py-3 shadow-sm">
              <p className="text-[13.5px] leading-relaxed text-[#1A2332]">
                Welcome to <span className="font-medium">Rajpurohit Perfumes</span> 🌿
                <br />
                Ask us about <span className="font-medium">Aventus</span>,{' '}
                <span className="font-medium">Rose &amp; Oud</span>,{' '}
                <span className="font-medium">Dark Secret</span> — or let us find your signature scent.
              </p>
              <span className="block mt-1.5 text-[10px] text-[#A09880] text-right">now</span>
            </div>
          </div>

          {/* Quick replies */}
          <div className="px-4 py-4 space-y-2">
            <p className="text-[10px] tracking-[.18em] uppercase text-[#A09880] mb-2.5">
              Pick one to get started
            </p>
            {CHIPS.map((c) => (
              <a key={c.label} href={waLink(c.msg)} target="_blank" rel="noopener noreferrer"
                 onClick={() => setOpen(false)}
                 className="flex items-center gap-2.5 w-full px-3.5 py-2.5 rounded-xl bg-white border border-[#E5DCC9] text-[13px] text-[#1A2332] hover:border-[#C9A961] hover:shadow-sm transition-all touch-manipulation">
                <span className="text-[15px] leading-none">{c.emoji}</span>
                <span className="flex-1 text-left">{c.label}</span>
              </a>
            ))}
          </div>

          {/* CTA */}
          <div className="px-4 pb-4">
            <a href={waLink('Hi Rajpurohit Perfumes!')} target="_blank" rel="noopener noreferrer"
               onClick={() => setOpen(false)}
               className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-[#25D366] hover:bg-[#20bd5a] text-white text-[13px] font-medium tracking-wide transition-colors touch-manipulation">
              <Send className="w-4 h-4" />
              Start chat on WhatsApp
            </a>
          </div>
        </div>
      )}

      {/* ── COLLAPSED PILL ── */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Chat with us on WhatsApp"
        className={`group flex items-center gap-2.5 pl-1.5 pr-4 py-1.5 rounded-full shadow-[0_10px_32px_rgba(37,211,102,0.35)] transition-all duration-500 touch-manipulation ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3 pointer-events-none'
        } ${open ? 'scale-95' : 'hover:scale-[1.03]'}`}
        style={{ background: 'linear-gradient(135deg,#25D366 0%,#1eb455 100%)' }}
      >
        {/* Icon badge */}
        <span className="relative w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0">
          <svg viewBox="0 0 24 24" className="w-[22px] h-[22px] fill-[#25D366]">
            <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15s-.77.96-.94 1.16c-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.39-1.47-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.01-1.04 2.47s1.06 2.86 1.21 3.06c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.19 1.87.12.57-.09 1.76-.72 2-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35z"/>
            <path d="M12 2C6.48 2 2 6.48 2 12c0 1.77.46 3.42 1.27 4.86L2 22l5.28-1.24A9.94 9.94 0 0 0 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm0 18.2c-1.55 0-3-.44-4.23-1.2l-.3-.18-3.13.74.75-3.05-.2-.31A8.15 8.15 0 0 1 3.8 12c0-4.52 3.68-8.2 8.2-8.2s8.2 3.68 8.2 8.2-3.68 8.2-8.2 8.2z"/>
          </svg>
          {/* notification dot */}
          {!open && (
            <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-[#C9A961] border-2 border-white" />
          )}
        </span>

        {/* Rotating copy */}
        {!open && (
          <span className="text-left leading-tight py-0.5 min-w-[168px]">
            <span className="flex items-center gap-1.5 text-white/85 text-[10px] tracking-wide">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C9F7D4] animate-pulse" />
              {TEASERS[teaser].dot}
            </span>
            <span className="block text-white text-[12.5px] font-semibold whitespace-nowrap">
              {typed}
              <span className="inline-block w-[1px] h-[11px] bg-white/70 ml-px align-middle animate-pulse" />
            </span>
          </span>
        )}
      </button>

      <style>{`
        @keyframes waIn {
          from { opacity: 0; transform: translateY(12px) scale(.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1);  }
        }
      `}</style>
    </div>
  );
}
